import { THEME_KEYS, type ThemeKey } from "@sparklab/types";

export const DEFAULT_THEME: ThemeKey = "bright";

export interface ThemeStyles {
  bg: string;
  card: string;
  header: string;
  subheader: string;
  button1: string;
  button2: string;
  button3: string;
  button4: string;
  exploreCard: string;
  input: string;
  button: string;
  listItem: string;
  preview: string;
}

export const THEME_OPTIONS = [...THEME_KEYS] as ThemeKey[];

export const themeConfigs: Record<ThemeKey, ThemeStyles> = {
  bright: {
    bg: "bg-[#1e1b4b]",
    card: "bg-[#312e81] border-[#a78bfa]",
    header: "text-[#a5f3fc]",
    subheader: "text-[#fef9c3]",
    button1: "bg-[#4338ca] border-[#f472b6]",
    button2: "bg-[#4f46e5] border-[#fef9c3]",
    button3: "bg-[#7c3aed] border-[#fdba74]",
    button4: "bg-[#5b21b6] border-[#86efac]",
    exploreCard: "bg-[#2e1065] border-[#a78bfa]",
    input: "bg-[#1e1b4b] border-[#a78bfa] text-white",
    button: "bg-[#4338ca] border-[#f472b6]",
    listItem: "bg-[#4c1d95] border-[#a78bfa]",
    preview: "bg-white",
  },
  sunset: {
    bg: "bg-[#FF8C42]",
    card: "bg-[#FF5F5F] border-[#FFD93D]",
    header: "text-white",
    subheader: "text-[#FFF5CE]",
    button1: "bg-[#FF3F3F] border-[#FFE156]",
    button2: "bg-[#FF7F32] border-[#FFFFFF]",
    button3: "bg-[#FFB037] border-[#FFE156]",
    button4: "bg-[#FF5959] border-[#C1FBA4]",
    exploreCard: "bg-[#FF7B7B] border-[#FFD93D]",
    input: "bg-[#FF7B7B] border-[#FFD93D] text-white",
    button: "bg-[#FF3F3F] border-[#FFE156]",
    listItem: "bg-[#FF7B7B] border-[#FFD93D]",
    preview: "bg-white",
  },
  mint: {
    bg: "bg-[#3EB489]",
    card: "bg-[#2E8B57] border-[#A7FFEB]",
    header: "text-white",
    subheader: "text-[#E8FFF8]",
    button1: "bg-[#2F7D5A] border-[#A7FFEB]",
    button2: "bg-[#4CC9A7] border-white",
    button3: "bg-[#00A97F] border-[#FFD166]",
    button4: "bg-[#248A6D] border-[#CAFFBF]",
    exploreCard: "bg-[#2B7A59] border-[#A7FFEB]",
    input: "bg-[#2B7A59] border-[#A7FFEB] text-white",
    button: "bg-[#2F7D5A] border-[#A7FFEB]",
    listItem: "bg-[#2B7A59] border-[#A7FFEB]",
    preview: "bg-white",
  },
  rose: {
    bg: "bg-[#3f051c]",
    card: "bg-[#5c0a2f] border-[#f472b6]",
    header: "text-[#fecdd3]",
    subheader: "text-[#ffe4f0]",
    button1: "bg-[#7a143d] border-[#ff9ac6]",
    button2: "bg-[#a01b56] border-white",
    button3: "bg-[#c9184a] border-[#ffe066]",
    button4: "bg-[#8d1e5b] border-[#c1fba4]",
    exploreCard: "bg-[#5b0a31] border-[#f472b6]",
    input: "bg-[#5b0a31] border-[#f472b6] text-white",
    button: "bg-[#7a143d] border-[#ff9ac6]",
    listItem: "bg-[#5b0a31] border-[#f472b6]",
    preview: "bg-white",
  },
  aurora: {
    bg: "bg-[#082f49]",
    card: "bg-[#0f3b57] border-[#a5f3fc]",
    header: "text-[#bef264]",
    subheader: "text-[#d9f99d]",
    button1: "bg-[#0d9488] border-[#fde68a]",
    button2: "bg-[#1d4ed8] border-[#a5f3fc]",
    button3: "bg-[#2563eb] border-[#f472b6]",
    button4: "bg-[#0f766e] border-[#86efac]",
    exploreCard: "bg-[#113955] border-[#a5f3fc]",
    input: "bg-[#113955] border-[#a5f3fc] text-white",
    button: "bg-[#0d9488] border-[#fde68a]",
    listItem: "bg-[#113955] border-[#a5f3fc]",
    preview: "bg-white",
  },
  midnight: {
    bg: "bg-[#0b1221]",
    card: "bg-[#111a2f] border-[#94a3b8]",
    header: "text-[#c7d2fe]",
    subheader: "text-[#e0e7ff]",
    button1: "bg-[#1e293b] border-[#facc15]",
    button2: "bg-[#0f172a] border-[#38bdf8]",
    button3: "bg-[#1d4ed8] border-[#f472b6]",
    button4: "bg-[#0f172a] border-[#34d399]",
    exploreCard: "bg-[#141d33] border-[#94a3b8]",
    input: "bg-[#141d33] border-[#94a3b8] text-white",
    button: "bg-[#1e293b] border-[#facc15]",
    listItem: "bg-[#141d33] border-[#94a3b8]",
    preview: "bg-white",
  },
};

export function getThemeStyles(theme?: ThemeKey): ThemeStyles {
  return themeConfigs[theme ?? DEFAULT_THEME];
}

